// Autosave + crash recovery (#46).
//
// The user expects to come back to an unsaved document if their tab crashed
// or they accidentally closed the window. Photoshop does this with recovery
// files; we do it with IndexedDB.
//
// Storage layout (one IDB database `pixelpa-autosave`):
//   - object store `snapshots` keyed by `docId` (string).
//     Each value is { docId, savedAt: number (ms epoch), bytes: ArrayBuffer }
//   - the bytes are the document serialized as a .pxa blob (lossless).
//
// Lifecycle:
//   1. `installAutosave(state, opts)` subscribes to state changes. Every
//      command flips `state.dirty` to true. On each state notification we
//      debounce a save: write to IDB after `intervalMs` of inactivity, OR at
//      most every `intervalMs` while dirtying continuously.
//   2. `state.markSaved()` (called by the explicit Save action) clears
//      `state.dirty`. We do NOT delete the snapshot here — the user might
//      reload before the explicit save completes, in which case the snapshot
//      is still a valid recovery.
//   3. `loadRecoverySnapshot()` is called at boot. If a snapshot exists, it
//      is returned to the app; the app shows a toast offering to recover.
//   4. `discardRecoverySnapshot()` removes the snapshot.
//
// Architecture: this module lives in `src/app/` because it touches IndexedDB
// (a browser API), and IDB is not allowed in `src/core/`.

import type { EditorState } from "./state/EditorState";
import type { Document } from "../core/document/Document";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DB_NAME = "pixelpa-autosave";
const STORE_NAME = "snapshots";
const DB_VERSION = 1;
/** Default debounce interval between writes. */
const DEFAULT_INTERVAL_MS = 30_000;
/** localStorage key for the active document id. */
const LS_ACTIVE_DOC_ID = "pixelpa-active-doc-id";

export interface AutosaveSnapshot {
  docId: string;
  savedAt: number;
  bytes: ArrayBuffer;
}

export interface AutosaveOptions {
  /** Function to serialize a Document into a .pxa blob. */
  serialize: (doc: Document) => Promise<Blob>;
  /** Function to deserialize a .pxa blob into a Document. */
  deserialize: (blob: Blob) => Promise<Document>;
  /** Function the autosave can call to show a recovery prompt. */
  showRecoveryToast: (snapshot: AutosaveSnapshot) => void;
  /** Override of the debounce interval. Defaults to 30 s. */
  intervalMs?: number;
  /**
   * Active doc id. The autosaver uses this to derive the IDB key. Defaults
   * to a localStorage-persisted UUID so that the recovery survives reloads.
   */
  docId?: string;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export class AutosaveManager {
  private state: EditorState;
  private opts: AutosaveOptions;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private lastSaveAt = 0;
  private docId: string;
  private unsubscribe: (() => void) | null = null;
  private inflight = false;
  private pending = false;
  private disposed = false;

  constructor(state: EditorState, opts: AutosaveOptions) {
    this.state = state;
    this.opts = opts;
    this.docId = opts.docId ?? this.getOrCreateDocId();
  }

  /**
   * Begin observing the editor state. Returns a dispose function that
   * unsubscribes and clears any pending timer.
   */
  install(): () => void {
    this.unsubscribe = this.state.subscribe(() => this.onStateChange());
    // i260: flush any pending save best-effort before the tab actually goes
    // away. IndexedDB writes are fundamentally asynchronous — nothing can
    // make them truly synchronous inside `beforeunload` — so instead of
    // relying on that one event alone (unreliable on mobile Safari, and
    // fired only at the very last moment before teardown) we ALSO flush on
    // `pagehide` and on `visibilitychange` -> "hidden". Both fire earlier
    // and more reliably than `beforeunload` when a tab is backgrounded,
    // frozen, or discarded by the OS/BFCache, giving the async write more
    // time to land before the page is actually torn down.
    if (typeof window !== "undefined") {
      const flush = () => { void this.flushNow(); };
      const onVisibilityChange = () => {
        if (typeof document !== "undefined" && document.visibilityState === "hidden") flush();
      };
      window.addEventListener("beforeunload", flush);
      window.addEventListener("pagehide", flush);
      if (typeof document !== "undefined") {
        document.addEventListener("visibilitychange", onVisibilityChange);
      }
      const off = this.unsubscribe;
      this.unsubscribe = () => {
        off?.();
        window.removeEventListener("beforeunload", flush);
        window.removeEventListener("pagehide", flush);
        if (typeof document !== "undefined") {
          document.removeEventListener("visibilitychange", onVisibilityChange);
        }
      };
    }
    return () => this.dispose();
  }

  dispose(): void {
    this.disposed = true;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.unsubscribe?.();
    this.unsubscribe = null;
  }

  /**
   * Try to load a recovery snapshot (if any) at boot and return it. Does NOT
   * apply it to the state — the app must decide whether to recover or
   * discard. Returns null if no snapshot exists for the active doc id.
   */
  async loadRecoverySnapshot(): Promise<AutosaveSnapshot | null> {
    const db = await this.openDb();
    return new Promise<AutosaveSnapshot | null>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(this.docId);
      req.onsuccess = () => {
        const v = req.result as AutosaveSnapshot | undefined;
        resolve(v ?? null);
      };
      req.onerror = () => reject(req.error);
    });
  }

  /** Remove the recovery snapshot. */
  async discardRecoverySnapshot(): Promise<void> {
    const db = await this.openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const req = store.delete(this.docId);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  /**
   * Apply a recovery snapshot to the editor state. Caller should confirm
   * with the user first; this overwrites the current document.
   */
  async applyRecoverySnapshot(snap: AutosaveSnapshot): Promise<void> {
    const blob = new Blob([snap.bytes]);
    const doc = await this.opts.deserialize(blob);
    this.state.setDocument(doc);
  }

  /** Force an immediate save, ignoring debounce. Used at unload time. */
  async flushNow(): Promise<void> {
    if (this.disposed) return;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    await this.save();
  }

  /** The document id this manager is currently autosaving/recovering. */
  get activeDocId(): string {
    return this.docId;
  }

  /**
   * i149 — reassign which document this manager autosaves/recovers. Call
   * this whenever the active document changes (e.g. the doc-tab click
   * handler in a multi-document session) so each open document autosaves
   * to — and recovers from — its own IndexedDB key, instead of every open
   * document sharing whatever single docId this manager started with.
   *
   * Any save currently in flight is unaffected: `save()` captures `docId`
   * into a local at its own start (before any `await`), so a doc switch
   * mid-save can never mislabel that snapshot with the new document's id.
   * A pending debounce timer is cleared — the newly-active document (or
   * the one just switched away from) gets a fresh onStateChange()-driven
   * schedule on its next edit.
   */
  setActiveDocId(docId: string): void {
    if (docId === this.docId) return;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.docId = docId;
  }

  // -------------------------------------------------------------------------
  // Internals
  // -------------------------------------------------------------------------

  private onStateChange(): void {
    if (this.disposed) return;
    if (!this.state.doc) return;
    if (!this.state.dirty) return;
    if (this.timer) return; // already scheduled

    const interval = this.opts.intervalMs ?? DEFAULT_INTERVAL_MS;
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.save();
    }, interval);
  }

  private async save(): Promise<void> {
    if (this.disposed) return;
    if (this.inflight) {
      // Save in progress — mark that we want another save when it finishes.
      this.pending = true;
      return;
    }
    const doc = this.state.doc;
    if (!doc) return;
    if (!this.state.dirty) return;
    // i149: capture the docId synchronously, before any `await` below. If
    // setActiveDocId() is called while this save is in flight (the user
    // switched document tabs mid-save), the snapshot we're already building
    // for `doc` must still be written under the docId it was captured
    // under — not silently relabeled as belonging to whichever document
    // becomes active later.
    const docId = this.docId;

    this.inflight = true;
    try {
      const blob = await this.opts.serialize(doc);
      const bytes = await blob.arrayBuffer();
      const snapshot: AutosaveSnapshot = {
        docId,
        savedAt: Date.now(),
        bytes,
      };
      await this.writeSnapshot(snapshot);
      this.lastSaveAt = snapshot.savedAt;
    } catch (err) {
      // Soft-fail: autosave failures should not bring down the editor. Log
      // for diagnostics.
      console.warn("[autosave] write failed:", err);
    } finally {
      this.inflight = false;
      if (this.pending) {
        this.pending = false;
        // Re-arm a save in case more dirties accumulated.
        this.onStateChange();
      }
    }
  }

  private async writeSnapshot(snapshot: AutosaveSnapshot): Promise<void> {
    const db = await this.openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const req = store.put(snapshot, snapshot.docId);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  private getOrCreateDocId(): string {
    if (typeof localStorage === "undefined") {
      // No localStorage (test env etc.) — use a session-only id.
      return "session";
    }
    let id = localStorage.getItem(LS_ACTIVE_DOC_ID);
    if (!id) {
      id = `doc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      try { localStorage.setItem(LS_ACTIVE_DOC_ID, id); } catch { /* ignore */ }
    }
    return id;
  }

  private dbPromise: Promise<IDBDatabase> | null = null;

  private openDb(): Promise<IDBDatabase> {
    if (this.dbPromise) return this.dbPromise;
    this.dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      if (typeof indexedDB === "undefined") {
        reject(new Error("IndexedDB not available"));
        return;
      }
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    return this.dbPromise;
  }
}

// ---------------------------------------------------------------------------
// Convenience: a default factory that uses the real .pxa serializer.
// ---------------------------------------------------------------------------

export async function installAutosaveWithDefaults(
  state: EditorState,
  showRecoveryToast: (snapshot: AutosaveSnapshot) => void,
  intervalMs: number = DEFAULT_INTERVAL_MS,
  docId?: string,
): Promise<{ manager: AutosaveManager; dispose: () => void }> {
  const { savePxa, openPxa } = await import("./io/pxa");
  const managerOpts: AutosaveOptions = {
    serialize: (doc) => savePxa(doc),
    deserialize: (blob) => openPxa(blob),
    showRecoveryToast,
    intervalMs,
    ...(docId !== undefined ? { docId } : {}),
  };
  const manager = new AutosaveManager(state, managerOpts);

  // Boot: check for a recovery snapshot and notify the host if one exists.
  // We do this BEFORE installing the change-listener so we don't accidentally
  // overwrite a fresh save with an old recovery.
  try {
    const snap = await manager.loadRecoverySnapshot();
    if (snap) showRecoveryToast(snap);
  } catch {
    // No DB / not available — skip silently.
  }

  const dispose = manager.install();
  return { manager, dispose };
}
